import React, {useState} from "react";
import {NavLink} from "react-router-dom";
import ContentBox from "../../../../../../components/content-box/ContentBox";
import PageActions from "../../../../../base/PageActions";
import Breadcrumbs from "../../../../../../components/breadcrumbs/Breadcrumbs";
import Modal from "../../../../../../components/modal/Modal";
import _image_service from "../../../../../../services/ImageService";
import '../../../../../styles/Common.scss';
import '../../WorldPages.scss';
import '../../WorldDynamicContent.scss';

interface EmpiricalHistoryPageProps {
}

const EmpiricalHistoryPage = (props: EmpiricalHistoryPageProps) => {

    const _map_src = `chase/world/geography/casarok_pre_cataclysm.png`;

    const page: PageActions = new PageActions({
        title: `Casarok History: Empirical Age - Chase the Fox`,
        description: "The Empirical Age of Casarok is the time the largest empires ruled over the world!",
        keywords: ["casarok", "casarok history", "empirical age", "fantasy timeline", "fantasy world history", "fantasy Roman age", "fantasy empires"],
        imgSrc: _map_src,
        url: `world/history/empirical-age`
    });

    const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);

    return page.generateMetaContent(
        <>
            <Modal isOpen={modalIsOpen}
                   onClose={() => setModalIsOpen(false)}>
                <div className="geography-modal-view">
                    <p className="center">
                        <a href={_image_service.getCdnImageSrc(_map_src, 'full')}
                           target="_blank"
                           rel="noopener noreferrer">
                            Click here to view the full size image.
                        </a>
                    </p>
                    <img src={_image_service.getCdnImageSrc(_map_src, 'desktop')}
                         title='Casarok Pre-Cataclysm Map'
                         alt="A map of Casarok before the Great Cataclysm"/>
                </div>
            </Modal>
            <ContentBox header="The Empirical Age of Casarok">
                <Breadcrumbs path={page.getUrl()}/>
                <div className="world-page-static">
                    <a id="visible-element"
                       className="switch-visible geography-modal-link"
                       onClick={() => setModalIsOpen(true)}>
                        <img className="block width-50-percent"
                             src={_image_service.getCdnImageSrc(`chase/world/geography/casarok_pre_cataclysm.png`)}
                             title='Casarok Pre-Cataclysm Map'
                             alt="A map of Casarok before the Great Cataclysm"/>
                    </a>
                </div>
                <div className="world-page-static">
                    <p className="center">
                        <em>This age covers the years of roughly 800 - 550 BCE.</em>
                    </p>
                    <p>
                        The Empirical Age is attributed to the time in which the seven strongest nations formed into
                        empires that divided up the ancient world. Once the empires' borders had settled, a tentative
                        peace washed over the world.
                    </p>
                    <ul className="spaced">
                        <li>
                            Populis Empire: The largest of the seven formed in central Casarok by
                            various <NavLink className="world-link" to="/world/bestiary/anthro">Anthro</NavLink> ruling
                            families. At it's peak, it spanned all of central Casarok, only stopping at major mountain
                            ranges, deserts, and forests that provided neighboring nations maximum defensive positions.
                        </li>
                        <li>
                            Kulong Dynasties: The empires that took over the eastern part of Casarok, mostly in what is
                            now <NavLink className="world-link" to="/world/history/modern-age">modern day Euraline</NavLink>. Their
                            dominance was led by unrivaled advancements in science, technology, and medicine.
                        </li>
                        <li>
                            Sequinid Pact: The group of nations that took over the entire portion of Casarok west of the
                            Populis Empire. They attributed their power and success to the blessings of
                            the <NavLink className="world-link" to="/world/religion/pantheon">gods and goddesses</NavLink> of
                            the world; however, their power in he realm of magic and healing was truly unparalleled.
                        </li>
                        <li>
                            Northern Coalition: Another group of nations in the Snowscape of northern Casarok which
                            banded together to prevent the Populis Empire from further encroaching on their lands.
                            They were proud warriors who values strength and athleticism over all else.
                        </li>
                        <li>
                            Southern Halidoms: The countries that mostly controlled southern Casarok. They were mostly
                            trade and culture empires, but they had the backing of the militarily powerful Pherulan
                            nation in the southeast.
                        </li>
                        <li>
                            Imperium Coreptuum: The nation growing around Mt. Coreptus that formed the largest empire
                            of <NavLink className="world-link" to="/world/bestiary/draconic/dracamma">dracammas</NavLink> peoples.
                            They are an extreme warrior culture and practice mostly a Spartan way of life, which fueled
                            their aggression and growing empire.
                        </li>
                        <li>
                            Saliman Empire: A small nation in the wastelands of the Firelands that grew outward through
                            their unique prowess with fire. Formed almost entirely by shunned
                            salamander <NavLink className="world-link" to="/world/bestiary/aspect">Aspects</NavLink>, they
                            grew into a mighty nation which expertly controlled fire, allied itself with the Imperium
                            Coreptuum, and became a force that antagonized the Populis Empire.
                        </li>
                    </ul>
                    <p>
                        The peace of this age was broken by the actions of the Saliman Empire. King Saliman XIII
                        intended to use the blessing of the goddess of fire, Blaze, along with powerful ancient fire
                        magic artifacts to enact a scorched earth policy which would incinerate and devastate large
                        swathes of the world to expand the Firelands and their empire. Their plan was discovered and
                        foiled by the last great emperor of the Populis Empire, but it wasn't without cost. The war
                        between the Populis and Saliman Empires spent many of their resources. Border disputes became
                        inflamed and the Populis Empire, which had already expanded to the point by which is was nearly
                        unmanageable, began to crumble. In fact, all of the large empires that had controlled the world
                        fell around the end of this age or beginning of the next.
                    </p>
                    <p>
                        <em>How does Casarok's Empirical Age differ from the time of the Roman empire on Earth?</em><br/>
                        A global threat emerged, the first since
                        the <NavLink className="world-link" to="/world/history/draconic-age">Draconic Age</NavLink>, required
                        cooperation between several empires to prevent. Aside from that potentially world-ending event,
                        the empires of the world had land access to one another. The empires in the early Americas would
                        not have contact with Europe and Asia until later in Earth's history as opposed to the
                        Pangaea-like continent in this era of Casarok which gave everyone ease of access to the trade
                        and culture of their neighbors.
                    </p>
                    <h3 className="headline">Notable Events</h3>
                    <ul className="spaced">
                        <li>
                            <em>789 BCE</em>: The Populis Empire forms and begins to expand its lands.
                        </li>
                        <li>
                            <em>762 BCE</em>: The other empires and coalitions form to counteract the growing threat of
                            the Populis Empire and Imperium Coreptuum.
                        </li>
                        <li>
                            <em>717 BCE</em>: The emperor of the Populis Empire most known for the time of its fastest
                            expansion is assassinated by his senators.
                        </li>
                        <li>
                            <em>664 BCE</em>: The seven great empires are solidified, and their borders expand to their
                            maximums as a tense but peaceful time washes over the land.
                        </li>
                        <li>
                            <em>641 BCE</em>: The Populis Empire forms the First Great Council of Magic to create laws
                            and regulations regarding the use of magic in the world.
                        </li>
                        <li>
                            <em>599 BCE</em>: King Saliman XIII is crowned the king of the Saliman Empire. He begins to
                            form a plan to destroy the world and reforge a new one in fire.
                        </li>
                        <li>
                            <em>592 BCE</em>: The Saliman Empire begins to gather artifacts of the goddess of fire. This
                            action along with their use of fire to commit war crimes catches the attention of the First
                            Great Council of Magic.
                        </li>
                        <li>
                            <em>591 BCE</em>: The emperor of the Populis Empire is assassinated by agents of the
                            Saliman Empire.
                        </li>
                        <li>
                            <em>588 BCE</em>: The Populis Empire attempts to defend their land from the growing threat
                            of the Saliman Empire, but they cannot take aggressive action without weakening their
                            borders or entering open conflict with Imperium Coreptuum.
                        </li>
                        <li>
                            <em>586 BCE</em>: The Populis and Saliman Empires enter open conflict upon discovering the
                            true intentions of King Saliman XIII.
                        </li>
                        <li>
                            <em>583 BCE</em>: The great empires of the world defeat King Saliman XIII. He is put on
                            trial and executed for his crimes.
                        </li>
                        <li>
                            <em>573 BCE</em>: The borders of the Populis Empire face scrutiny as territorial disputes
                            erupt on all sides with the other empires.
                        </li>
                        <li>
                            <em>566 BCE</em>: The borders of the Populis Empire begin rapid decline.
                        </li>
                        <li>
                            <em>559 BCE</em>: Primitive gunpowder is invented during the last Kulong Dynasty. It is only
                            used in early fireworks.
                        </li>
                        <li>
                            <em>555 BCE</em>: The Populis Empire falls. Its land is divided among the many conquering
                            peoples. Casarok enters a dark age.
                        </li>
                    </ul>
                </div>
            </ContentBox>
        </>
    );
}

export default EmpiricalHistoryPage;